import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  PlusIcon,
  Eye,
  Edit,
  User,
  Crown,
  MapPin,
  Phone,
  Calendar,
  Search,
  Mail,
} from "lucide-react";

import { Button } from "@shared/ui/button";
import { Switch } from "@shared/ui/switch";
import { useToast } from "@shared/hooks/use-toast";
import {
  FieldTooltip,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { FilterConfig, FilterValues } from "@shared/types/tableFilters";
import { PAGE_SIZE } from "@shared/utils/constants";
import FieldForceDashboard from "../fieldForceDashboard/fieldForceDashboard";
import {
  useFieldForceExport,
  useGetFieldForceData,
  useUpdateFieldForceStatus,
} from "../../hooks";
import FieldForceAddModal from "./FieldForceAddModal/fieldForceAddModal";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Input } from "@shared/ui/input";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";
import { Badge } from "@shared/ui/badge";
import { FieldForceFiltersPanel } from "./filter";
import { useChoices } from "../../hooks";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { useFacilityUtilityServices } from "@features/ax/assetRegistry/facilities/hooks";

// Updated AddTechnicianButton to accept onClick prop
const AddTechnicianButton = React.forwardRef<
  HTMLButtonElement,
  { onClick?: () => void }
>(({ onClick, ...props }, ref) => {
  return (
    <Button
      ref={ref}
      onClick={onClick}
      className="bg-blue-600 hover:bg-blue-700"
      {...props}
    >
      <PlusIcon className="h-4 w-4 mr-2" />
      Add Technician
    </Button>
  );
});

AddTechnicianButton.displayName = "AddTechnicianButton";

interface FieldForceData {
  id: string;
  fieldForceNo: string;
  ffId: string;
  name: string;
  contact: string;
  email: string;
  role: "Technician" | "Supervisor";
  zone: string;
  area: string;
  joiningDate: string;
  status: "Active" | "Inactive";
  assignedSO: number;
  completedSO: number;
  isActive: boolean;
}

const FieldForceLanding: React.FC = () => {
  usePageViewTimer({ pageName: "WX Field Force" });
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const updateStatus = useUpdateFieldForceStatus();
  const [itemsPerPage] = useState(PAGE_SIZE);
  const [searchParams, setSearchParams] = useSearchParams();

  // Read all params from URL
  const searchQuery = decodeURIComponent(searchParams.get("query") || "");
  const sopStatus = searchParams.get("sopStatus") || "";
  const priority = searchParams.get("priority") || "";
  const utilityService = searchParams.get("utilityService") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  const [pendingFilters, setPendingFilters] = useState({
    sopStatus: decodeURIComponent(sopStatus),
    priority: decodeURIComponent(priority),
    utilityService: decodeURIComponent(utilityService),
  });

  useEffect(() => {
    setPendingFilters({
      sopStatus: decodeURIComponent(sopStatus),
      priority: decodeURIComponent(priority),
      utilityService: decodeURIComponent(utilityService),
    });
  }, [sopStatus, priority, utilityService]);

  const activeFilters = useMemo(() => {
    const f: any = {};
    if (sopStatus) f.sopStatus = sopStatus;
    if (priority) f.priority = priority;
    if (utilityService) f.utilityService = utilityService;
    return f;
  }, [sopStatus, priority, utilityService]);

  const { data: sopStatusChoice } = useChoices("sop_status");
  const { data: priorityChoice } = useChoices("priority");

  const sopStatusList = useMemo(() => {
    return mapToLabelValue(sopStatusChoice?.result || sopStatusChoice || []);
  }, [sopStatusChoice]);

  const { data: utilityServicesData } = useFacilityUtilityServices();

  const priorityList = useMemo(() => {
    return mapToLabelValue(priorityChoice?.result || priorityChoice || []);
  }, [priorityChoice]);

  const { data: getItems, refetch } = useGetFieldForceData(
    currentPage,
    PAGE_SIZE,
    searchQuery,
    activeFilters,
  );

  const { exportCSV, isExporting } = useFieldForceExport(
    remoteUtilityId,
    searchQuery,
    activeFilters,
  );

  const handleExport = () => {
    exportCSV();
  };

  useEffect(() => {
    refetch(); // Just refetch the current query
  }, [location.pathname]);
  // Add state for modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  useEffect(() => {
    if (getItems) {
      const totalItems = getItems.count || 0;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      const startItem = (currentPage - 1) * itemsPerPage + 1;
      const endItem = Math.min(currentPage * itemsPerPage, totalItems);

      setPagination({
        currentPage: getItems.page_number || currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        startItem: totalItems > 0 ? startItem : 0,
        endItem: totalItems > 0 ? endItem : 0,
      });
    }
  }, [getItems, currentPage, itemsPerPage]);

  const handlePageChange = async (page: number) => {
    if (page !== currentPage && page >= 1 && page <= pagination.totalPages) {
      const newParams: Record<string, string> = {
        ...Object.fromEntries(searchParams.entries()),
        page: page.toString(),
      };
      setSearchParams(newParams);
    }
  };

  // Handle Add Technician button click
  const handleAddTechnicianClick = () => {
    setIsModalOpen(true);
  };

  // Handle modal redirect
  const handleModalRedirect = () => {
    setIsModalOpen(false);
    navigate("/utility/utility-setup/user-management");
  };

  const handleSearchChange = (query: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(sopStatus && { sopStatus }),
      ...(priority && { priority }),
      ...(utilityService && { utilityService }),
    };

    if (query.trim()) {
      newParams.query = query.trim();
    } else {
      delete newParams.query; // remove from URL if search is empty
    }

    // Reset to page 1 on search
    newParams.page = "1";

    setSearchParams(newParams);
  };

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.sopStatus;
    delete newParams.priority;
    delete newParams.utilityService;

    setSearchParams(newParams);
  };

  const filterPanel = (
    <FieldForceFiltersPanel
      sopStatus={sopStatus}
      priority={priority}
      utilityService={utilityService}
      sopStatusList={sopStatusList}
      priorityList={priorityList}
      utilityServicesData={utilityServicesData}
      onApplyFilters={(filters) => {
        const newParams: any = {
          ...Object.fromEntries(searchParams.entries()),
          ...(filters.sopStatus && { sopStatus: filters.sopStatus }),
          ...(filters.priority && { priority: filters.priority }),
          ...(filters.utilityService && { utilityService: filters.utilityService }),
          page: "1", // reset page
        };
        setSearchParams(newParams);
      }}
      onResetFilters={resetFilters}
    />
  );

  const hasActiveFilters = !!sopStatus || !!priority || !!utilityService;

  const handleToggleStatus = async (row: FieldForceData, checked: boolean) => {
    const payload = {
      id: row.id,
      is_active: checked,
      remote_utility_id: remoteUtilityId,
    };
    updateStatus.mutateAsync(payload);
  };

  const columns: TableColumn<FieldForceData>[] = [
    {
      key: "fieldForceNo",
      header: "EMP ID",
      sortable: true,
      width: "w-20",
    },
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (value: string, row: FieldForceData) => (
        <span className="font-medium">{value}</span>
      ),
    },
    {
      key: "contactNumber",
      header: "Contact",
      sortable: true,
      render: (value: string, row) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span>{value || "-"}</span>
          </div>

          <div className="flex items-center gap-2">
            <span>{row.email || "-"}</span>
          </div>
        </div>
      ),
    },
    {
      key: "roleName",
      header: "Role",
      sortable: true,
      render: (value: string) => (
        <div className="flex items-center gap-2">
          {value === "Technician" && (
            <User className="h-4 w-4 text-orange-500" />
          )}
          {value === "Supervisor" && (
            <Crown className="h-4 w-4 text-purple-500" />
          )}
          <span>{value}</span>
        </div>
      ),
    },
    {
      key: "areaNames",
      header: "Area",
      sortable: true,
      render: (value: string[]) => (
        <div className="flex items-center gap-2">
          <span>{value.map((area: string) => area).join(", ") || "N/A"}</span>
        </div>
      ),
    },
    {
      key: "subAreaNames",
      header: "Sub Area",
      sortable: true,
      render: (value: string[]) => (
        <div className="flex items-center gap-2">
          <span>
            {value.map((subArea: string) => subArea).join(", ") || "N/A"}
          </span>
        </div>
      ),
    },
    {
      key: "total",
      header: "Assigned SO",
      sortable: true,
      width: "w-24",
    },
    {
      key: "completed",
      header: "Completed SO",
      sortable: true,
      width: "w-24",
    },
    {
      key: "createdUserRemoteName",
      header: "Created By",
      sortable: true,
    },
    {
      key: "createdDate",
      header: "Created On",
      sortable: true,
    },
    {
      key: "isActive",
      header: "Status",
      sortable: true,
      render: (value: boolean) => (
        //use badge instead of this
        <Badge variant={value ? "default" : "secondary"}>
          {value ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      key: "isUserAccess",
      header: "App Account",
      render: (value, row: any) => (
        <Badge variant={row.isUserAccess ? "default" : "secondary"}>
          {row.isUserAccess ? "Enabled" : "Disabled"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      sortable: false,
      width: "w-32",
      render: (value: boolean, row: FieldForceData) => (
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() =>
                    navigate(
                      `/service-orders/field-force/view/${row.id}/${row.fieldForceNo}/`,
                    )
                  }
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Eye className="h-4 w-4 text-gray-500" />
                </button>
              </TooltipTrigger>
              <FieldTooltip>View Details</FieldTooltip>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() =>
                    navigate(
                      `/service-orders/field-force/edit/${row.id}/${row.fieldForceNo}`,
                    )
                  }
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <Edit className="h-4 w-4 text-gray-500" />
                </button>
              </TooltipTrigger>
              <FieldTooltip>Edit</FieldTooltip>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2">
                  <Switch
                    id={`technician-${row.id}`}
                    checked={row.isActive}
                    onCheckedChange={(checked) =>
                      handleToggleStatus(row, checked)
                    }
                  />
                </div>
              </TooltipTrigger>
              <FieldTooltip>
                {row.isActive ? "Inactive" : "Active"} Technician
              </FieldTooltip>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in mt-6 p-2">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="page-title">Field Force Management</h2>
          <p className="page-subtitle">
            Manage your technicians, supervisors, and field personnel
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <AddTechnicianButton onClick={handleAddTechnicianClick} />
            </TooltipTrigger>
            <FieldTooltip>
              Add a new technician or supervisor to your field force
            </FieldTooltip>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* Dashboard Cards */}
      <FieldForceDashboard />

      {/* Data Table */}
      <div className="mt-2">
        <GenericTable
          data={getItems?.results}
          columns={columns}
          rowKey="id"
          selectable={false}
          emptyMessage="No field force personnel found"
          pagination={pagination}
          onSearch={handleSearchChange}
          searchParamName="query"
          searchDebounceMs={300}
          searchPlaceholder="Search by EMP ID, Name, Email"
          onPageChange={handlePageChange}
          onExport={handleExport}
          exportLoading={isExporting}
          filterPanel={filterPanel}
          filters={
            hasActiveFilters ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {[!!sopStatus, !!priority, !!utilityService].filter(Boolean).length} filter(s) active
                </span>
              </div>
            ) : null
          }
        />
      </div>

      {/* Add Technician Modal */}
      <FieldForceAddModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        onRedirect={handleModalRedirect}
      />
    </div>
  );
};

export default FieldForceLanding;
